import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import { QueryOption } from '../@types';
import {
  PartObject,
  PartConditionDropdown,
  PartLocationObject,
  PartListResponse,
  PartHistoryDataType,
  NSPartLocationList,
  SerailizedPartsList,
  PartHistoryMovementListResponse,
  PartHistoryMovementListFilterOptions,
  MerchantDamageTemplateQuery,
  MerchantDamagePartSerialQueryData,
  DuplicatePartsDataType,
  PartHistory,
  UpdatePartLocationObject,
  HistoryType,
} from '../@types/part.type';
import { PartActionHistoryDataType } from '../@types/partHistory.type';
import { WarrantyPanalType } from '../@types/warrantySummary.type';
import {
  getAllWarrantySummary,
  getPartById,
  getPartConditions,
  getPartExistanceBySerials,
  getNSPartExistanceBySerials,
  getPartHistoryByPartId,
  getPartLocationDataById,
  updatePart,
  updatePartLocation,
  getPartLocationDataByServiceId,
  updateBulkPart,
  getPartMovementHistory,
  getPartHistoryByServiceIdAndHistoryTypeCodes,
  getDuplicatePartsBySerial1AndPartType,
  mergeDuplicateParts,
  createExceptionalPart,
  updateExceptionalPart,
  getPartRepairHistory,
  getLatestPartMomentHistory,
  getPartByIdForService,
  getPartConditionsForService,
  getPartLocationDataByIdForService,
  updatePartForService,
  processPartBulkUpdateFile,
  getPartLocationDataByPartId,
  getExistingSerials,
  getHistoryTypes,
} from '../api/part';
import {
  getAllParts,
  getNSPartByPartTypeAndLocation,
  getPartsLogAgainstService,
  getMerchantDamagePartSerialData,
  updatePartAdditionalData,
} from '../api/parts';
import { PartTypeLocationItemData } from '../components/molecules/PartTypeLocationItem';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';
import {
  PartKey,
  PartKeyForService,
  PartConditionKey,
  PartConditionKeyForService,
  HistoryTypesKey,
  WarrantySummaryKey,
  PartLocationKey,
  PartsKey,
  PartHistoryKey,
  PartExistanceKey,
  NSPartExistanceKey,
  NSPartLocationListKey,
  PartLocationsListKey,
  MerchantDamagePartSerialDataKey,
  PartHistoryListForServiceKey,
  DuplicatePartsKey,
  LatestPartMomentHistory,
  PartsLogAgainstServiceKey,
} from '../configs/part';
import { ServiceLogAllKey } from '../configs/services';
import { getSelectedColumnNames } from '../utils/columnSelectionOperations';

// `staleTime: 60_000` — mutations to this part explicitly invalidate this
// key, so a 1-minute floor just deduplicates the refetch storm that
// otherwise happens on window focus / remount. Longer would also be fine.
const PART_DETAIL_STALE_TIME_MS = 60 * 1000;

export const useGetOnePart = (id: number): UseQueryResult<PartObject | undefined> => {
  return useQuery({
    queryKey: [PartKey, id],
    queryFn: async ({ signal }) => {
      if (!id) return undefined;
      return getPartById(id, signal);
    },
    staleTime: PART_DETAIL_STALE_TIME_MS,
  });
};

export const useGetOnePartForService = (id: number): UseQueryResult<PartObject | undefined> => {
  return useQuery({
    queryKey: [PartKeyForService, id],
    queryFn: async ({ signal }) => {
      if (!id) return undefined;
      return getPartByIdForService(id, signal);
    },
    staleTime: PART_DETAIL_STALE_TIME_MS,
  });
};

export const useGetPartConditions = (): UseQueryResult<PartConditionDropdown[]> => {
  return useQuery({
    queryKey: [PartConditionKey],
    queryFn: ({ signal }) => getPartConditions(signal),
    staleTime: 5 * 60 * 60 * 1000,
  });
};

export const useGetHistoryTypes = (): UseQueryResult<HistoryType[]> => {
  return useQuery({
    queryKey: [HistoryTypesKey],
    queryFn: ({ signal }) => getHistoryTypes(signal),
    staleTime: 5 * 60 * 60 * 1000,
  });
};

export const useGetPartConditionsForService = (): UseQueryResult<PartConditionDropdown[]> => {
  return useQuery({
    queryKey: [PartConditionKeyForService],
    queryFn: ({ signal }) => getPartConditionsForService(signal),
    staleTime: 5 * 60 * 60 * 1000,
  });
};

export const useUpdatePart = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await updatePart(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [PartHistoryKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [WarrantySummaryKey, id],
      });
    },
  });
};

export const useUpdatePartForService = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await updatePartForService(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartKeyForService, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [PartHistoryKey, id],
      });
    },
  });
};

export const useGetAllWarrantySummary = (
  id: number
): UseQueryResult<WarrantyPanalType[] | undefined> => {
  return useQuery({
    queryKey: [WarrantySummaryKey, id],
    queryFn: async ({ signal }) => {
      if (!id) return undefined;
      return getAllWarrantySummary(id, signal);
    },
    staleTime: PART_DETAIL_STALE_TIME_MS,
  });
};

export const useGetPartLocationDataById = (
  id: number
): UseQueryResult<PartLocationObject | undefined> => {
  return useQuery({
    queryKey: [PartLocationKey, id],
    queryFn: async ({ signal }) => {
      if (!id) return undefined;
      return getPartLocationDataById(id, signal);
    },
  });
};

export const useGetPartLocationDataByPartId = (
  id: number,
  locationData: UpdatePartLocationObject
): UseQueryResult<PartLocationObject | undefined> => {
  return useQuery({
    queryKey: [PartLocationKey, id],
    queryFn: async ({ signal }) => {
      if (!id) return undefined;
      return getPartLocationDataByPartId(id, locationData, signal);
    },
    enabled: false,
  });
};

export const useGetPartLocationDataByIdForService = (
  id: number
): UseQueryResult<PartLocationObject | undefined> => {
  return useQuery({
    queryKey: [PartLocationKey, id],
    queryFn: async ({ signal }) => {
      if (!id) return undefined;
      return getPartLocationDataByIdForService(id, signal);
    },
  });
};

export const useGetPartLocationDataByServiceId = (
  partTypeId: number,
  serviceId: number
): UseQueryResult<PartTypeLocationItemData[]> => {
  return useQuery({
    queryKey: [PartLocationsListKey, partTypeId, serviceId],
    queryFn: ({ signal }) => getPartLocationDataByServiceId(partTypeId, serviceId, signal),
  });
};

export const useUpdatePartLocation = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await updatePartLocation(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartLocationKey, id],
      });
    },
  });
};

export const useGetAllParts = (
  queryOption: QueryOption,
  serialSearch?: string,
  columnSelectionItems?: ColumnSelectionItem[],
  obfuscation?: string
): UseQueryResult<PartListResponse> => {
  return useQuery({
    queryKey: [PartsKey, queryOption, obfuscation],
    queryFn: ({ signal }) =>
      getAllParts(
        queryOption,
        serialSearch,
        getSelectedColumnNames(columnSelectionItems),
        obfuscation,
        signal
      ),
    gcTime: 0,
    enabled: false,
  });
};

export const useGetPartHistoryByPartId = (id: number): UseQueryResult<PartHistoryDataType[]> => {
  return useQuery({
    queryKey: [PartHistoryKey, id],
    queryFn: ({ signal }) => getPartHistoryByPartId(id, signal),
    // BFF handler for /parts/:partId/history fires 2 extra queries per row
    // (createdBy node + attachments). Until the BFF resolves that N+1,
    // a short staleTime here is the cheapest way to avoid re-pulling the
    // full history on every window focus.
    staleTime: PART_DETAIL_STALE_TIME_MS,
  });
};

export const useGetPartExistanceBySerials = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      const response = await getPartExistanceBySerials(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartExistanceKey],
      });
    },
  });
};

export const useGetNSPartExistanceBySerials = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      const response = await getNSPartExistanceBySerials(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [NSPartExistanceKey],
      });
    },
  });
};

export const useGetNSPartByPartTypeAndLocation = (
  partTypeId: number,
  locationId: number,
  isReturn = false,
  isIncludeLocationChildren = false
): UseQueryResult<NSPartLocationList[]> => {
  return useQuery({
    // All four arguments feed the backend request, so they all need to be in
    // the cache identity — otherwise a second caller that flips `isReturn` or
    // `isIncludeLocationChildren` reuses the first caller's response.
    queryKey: [NSPartLocationListKey, partTypeId, locationId, isReturn, isIncludeLocationChildren],
    queryFn: ({ signal }) =>
      getNSPartByPartTypeAndLocation(
        partTypeId,
        locationId,
        isReturn,
        isIncludeLocationChildren,
        signal
      ),
    enabled: false,
  });
};

export const useUpdateBulkParts = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await updateBulkPart(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartsKey],
      });
    },
  });
};

export const useGetPartsLogAgainstService = (
  serviceId: number,
  locationTypeCode: string
): UseQueryResult<SerailizedPartsList[]> => {
  return useQuery({
    // Distinct key — previously this reused `[PartKey, id]`, which collides
    // with useGetOnePart when serviceId happens to equal a partId and also
    // silently drops `locationTypeCode` from the cache identity.
    queryKey: [PartsLogAgainstServiceKey, serviceId, locationTypeCode],
    queryFn: ({ signal }) => getPartsLogAgainstService(serviceId, locationTypeCode, signal),
  });
};

export const useGetPartMovementHistory = (
  queryOption: PartHistoryMovementListFilterOptions
): UseQueryResult<PartHistoryMovementListResponse | undefined> => {
  return useQuery({
    queryKey: [PartHistoryKey, queryOption],
    queryFn: async ({ signal }) => {
      const hasDateRange = queryOption.startDate && queryOption.endDate;
      const hasPartId = queryOption.partId;
      const hasCreatedAtFilter = queryOption?.filterModel?.items?.some(
        (filter) => filter?.field === 'createdAt'
      );
      // A free-text multi-search on its own is a valid query — allow it to fetch
      // even when the user has cleared the default Timestamp range. Trim so a
      // whitespace-only term (which getPartMovementHistory drops) can't activate
      // an unintended unfiltered fetch.
      const hasMultiSearch = !!queryOption.multiSearchQuery?.trim();

      if (!hasDateRange && !hasPartId && !hasCreatedAtFilter && !hasMultiSearch) {
        return undefined;
      }
      return getPartMovementHistory(queryOption, signal);
    },
    enabled: false,
    gcTime: 0,
  });
};

export const useGetMerchantDamagePartSerialDataQuery = (
  queryOption: MerchantDamageTemplateQuery
): UseQueryResult<MerchantDamagePartSerialQueryData[]> => {
  return useQuery({
    // serviceId + mainLocationNodeId are the two request discriminators on the
    // API — without them, switching service or main location silently reused
    // the prior response.
    queryKey: [
      MerchantDamagePartSerialDataKey,
      queryOption.serviceId,
      queryOption.mainLocationNodeId,
    ],
    queryFn: ({ signal }) => getMerchantDamagePartSerialData(queryOption, signal),
    gcTime: 0,
  });
};

export const useUpdatePartAdditionalData = (partId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await updatePartAdditionalData(data, partId);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey],
      });
    },
  });
};
export const useGetPartHistoryByServiceIdAndHistoryTypeCodes = (
  serviceId: number,
  historyTypes: string[]
): UseQueryResult<PartActionHistoryDataType[]> => {
  return useQuery({
    // historyTypes is part of the request shape — leaving it out of the key
    // meant two callers on the same serviceId with different type codes
    // could serve each other stale data.
    queryKey: [PartHistoryListForServiceKey, serviceId, historyTypes],
    queryFn: ({ signal }) =>
      getPartHistoryByServiceIdAndHistoryTypeCodes(serviceId, historyTypes, signal),
  });
};

export const useGetDuplicatePartsBySerial1AndPartType = (
  mainPartId: number,
  serial1: string,
  partTypeId: number
): UseQueryResult<DuplicatePartsDataType[] | undefined> => {
  return useQuery({
    // serial1 + partTypeId are part of the request — including them keeps the
    // cache entry specific to that serial/part-type combination so editing
    // either field shows a fresh duplicate list instead of the prior one.
    queryKey: [DuplicatePartsKey, mainPartId, serial1, partTypeId],
    queryFn: async ({ signal }) => {
      if (!mainPartId || !serial1 || !partTypeId) return undefined;
      return getDuplicatePartsBySerial1AndPartType(mainPartId, serial1, partTypeId, signal);
    },
  });
};

export const useGetPartRepairHistory = (partId: number): UseQueryResult<PartHistory[]> => {
  return useQuery({
    queryKey: [PartsKey, partId],
    queryFn: ({ signal }) => getPartRepairHistory(partId, signal),
  });
};

export const useGetLatestPartMomentHistory = (partId: number): UseQueryResult<PartHistory> => {
  return useQuery({
    queryKey: [LatestPartMomentHistory, partId],
    queryFn: ({ signal }) => getLatestPartMomentHistory(partId, signal),
  });
};

export const useMergeDuplicateParts = (mainPartId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await mergeDuplicateParts(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [WarrantySummaryKey, mainPartId],
      });
      await queryClient.invalidateQueries({
        queryKey: [PartHistoryKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [DuplicatePartsKey, mainPartId],
      });
    },
  });
};

export const useCreateExceptionalPart = (): UseMutationResult => {
  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await createExceptionalPart(data);
    },
  });
};

export const useUpdateExceptionalPart = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await updateExceptionalPart(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartHistoryKey],
      });
    },
  });
};

export const useProcessPartBulkUpdateFile = (): UseMutationResult => {
  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; typing the upload payload is a separate tidy-up PR
    mutationFn: async (fileList: any) => {
      return await processPartBulkUpdateFile(fileList);
    },
  });
};

export const useGetExistingSerials = (): UseMutationResult => {
  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexisting; each mutation takes a different payload shape, typing them precisely is a separate tidy-up PR
    mutationFn: async (data: any) => {
      return await getExistingSerials(data);
    },
  });
};
